/**
 * Locations Redux Slice
 *
 * Manages location selection for portal headers.
 * When a location is selected:
 * - If it has a factory → sets derivedFactoryId for X-Factory-Id header
 * - Sets currentLocationId for X-Location-Id header
 */

import { createSlice, type PayloadAction } from '@ufc/core'
import type { LocationSummary } from '../domain/types'
import { LOCATION_STORAGE_KEY } from '../domain/constants'

export interface LocationsState {
  /** Currently selected location for header */
  currentLocation: LocationSummary | null
  currentLocationId: string | null
  /** Factory documentId derived from the selected location (null if not a factory location) */
  derivedFactoryId: string | null
  /**
   * Every factory at the selected location.
   *
   * `null` means "not resolved yet" and an empty array means "resolved, and
   * this site has none". The difference matters: callers force an empty result
   * set when a site genuinely has no factory, and doing that before the answer
   * is known blanks the whole portal on first paint.
   */
  derivedFactoryIds: string[] | null
  /** Available locations for the header dropdown */
  locationsList: LocationSummary[]
  loading: boolean
  error: string | null
}

const initialState: LocationsState = {
  currentLocation: null,
  currentLocationId: null,
  derivedFactoryId: null,
  derivedFactoryIds: null,
  locationsList: [],
  loading: false,
  error: null,
}

export const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    /** Fetch locations for the header dropdown */
    fetchLocationsList: (state) => {
      state.loading = true
      state.error = null
    },
    fetchLocationsListSuccess: (state, action: PayloadAction<LocationSummary[]>) => {
      state.locationsList = action.payload
      state.loading = false

      // Re-derive the selection from the list that just arrived.
      //
      // The choice is restored from localStorage before the list is known, and
      // it carries a copy of the plant it derived at the time. That copy goes
      // stale: the site is still perfectly valid, but the plant id attached to
      // it may no longer be the right one — and because that id filters every
      // list and KPI in the portal, the whole thing reads zero with nothing on
      // screen to explain it. Reloading never clears it, because the stale
      // value is exactly what gets restored.
      //
      // So trust the site, not the copy: if the site is still in the list, take
      // its current plant; if it has gone, drop the selection entirely.
      if (state.currentLocationId && action.payload.length > 0) {
        const current = action.payload.find(
          (location) => location.documentId === state.currentLocationId,
        )

        if (current) {
          state.currentLocation = current
          state.derivedFactoryId = current.factoryDocumentId ?? null
          state.derivedFactoryIds = current.factoryDocumentIds ?? []
          if (typeof window !== 'undefined') {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(current))
          }
        } else {
          state.currentLocation = null
          state.currentLocationId = null
          state.derivedFactoryId = null
          state.derivedFactoryIds = null
          if (typeof window !== 'undefined') {
            localStorage.removeItem(LOCATION_STORAGE_KEY)
          }
        }
      }
    },
    fetchLocationsListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    /**
     * Select a location (or null for "All Locations" view)
     * Also derives the factoryId if the location belongs to a factory.
     */
    selectLocation: (state, action: PayloadAction<LocationSummary | null>) => {
      const location = action.payload
      state.currentLocation = location
      state.currentLocationId = location?.documentId ?? null
      state.derivedFactoryId = location?.factoryDocumentId ?? null
      state.derivedFactoryIds = location ? (location.factoryDocumentIds ?? []) : null

      if (typeof window !== 'undefined') {
        if (location) {
          localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location))
        } else {
          localStorage.removeItem(LOCATION_STORAGE_KEY)
        }
      }
    },

    /** Clear location selection */
    clearLocationSelection: (state) => {
      state.currentLocation = null
      state.currentLocationId = null
      state.derivedFactoryId = null
      state.derivedFactoryIds = null

      if (typeof window !== 'undefined') {
        localStorage.removeItem(LOCATION_STORAGE_KEY)
      }
    },

    /** Restore selection from localStorage on app init */
    restoreLocationFromStorage: (state) => {
      if (typeof window === 'undefined') return

      const stored = localStorage.getItem(LOCATION_STORAGE_KEY)
      if (stored) {
        try {
          const location = JSON.parse(stored) as LocationSummary
          state.currentLocation = location
          state.currentLocationId = location.documentId

          // The site is restored, but the plant it derived is deliberately not.
          //
          // That plant id filters every list and KPI in the portal, and the
          // stored copy may be stale — the site can now host several plants, or
          // a different one. Restoring it means the first render of every page
          // load queries against an unverified filter, and if it is wrong the
          // whole portal reads zero before anything can correct it.
          //
          // Leaving it null until the locations list confirms the current plant
          // fails open: the first paint is unfiltered, then narrows once the
          // answer is known. Showing too much briefly is recoverable; showing
          // nothing looks like the system is broken.
          state.derivedFactoryId = null
          state.derivedFactoryIds = null
        } catch {
          localStorage.removeItem(LOCATION_STORAGE_KEY)
        }
      }
    },

    /** Reset all location context */
    resetLocationContext: (state) => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(LOCATION_STORAGE_KEY)
      }
      state.currentLocation = null
      state.currentLocationId = null
      state.derivedFactoryId = null
      state.derivedFactoryIds = null
      state.locationsList = []
      state.loading = false
      state.error = null
    },
  },
})

export const locationsActions = locationsSlice.actions
export const locationsReducer = locationsSlice.reducer
